import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import AircraftMarker from './AircraftMarker';
import ReceiverMarker from './ReceiverMarker';
import AirportMarker from './AirportMarker';
import FurthestDistanceMarker from './FurthestDistanceMarker';
import AircraftTrack from './AircraftTrack';
import AircraftInfoPopup from './AircraftInfoPopup';
import AirportDetailOverlay from './AirportDetailOverlay';
import NotamOverlay from './NotamOverlay';
import { getAccessToken } from '../utils/auth';
import { getAirportsInBounds, getAircraftTracks, getTrackPoints, getNearestAirport, getAirportFeatures } from '../utils/httpApi';
import { MAP_STYLES, getMapFilter, saveColourSettings, getColourSettings } from '../utils/mapStyles';
import { ReceiverChip, ReceiverOptions } from './ReceiverPicker';
import { useMapSettings, useColourSettings } from '../contexts/SettingsContext';
import { debugLog } from '../utils/debug';
import { recordDistanceKm, pickTrackForRecord } from '../utils/rangeRecords';

// Track phase colors for ground operations (matches Airport.js)
const TRACK_PHASE_COLORS = {
  APPROACH: '#3b82f6',      // Blue
  CLIMB: '#3b82f6',         // Blue
  TOUCHDOWN: '#f97316',     // Orange
  TAKEOFF_ROLL: '#f97316',  // Orange
  ROLLOUT: '#eab308',       // Yellow
  LINEUP: '#eab308',        // Yellow
  TAXI_IN: '#22c55e',       // Green
  TAXI_OUT: '#22c55e',      // Green
  GATE_ARRIVAL: '#a855f7',  // Purple
  PUSHBACK: '#a855f7',      // Purple
  HOLDING: '#ef4444'        // Red
};

// Component to handle map events and send airport requests via HTTP API
function MapEventHandler({ onAirportsUpdate }) {
  const map = useMap();
  const debounceTimerRef = useRef(null);

  const sendAirportRequest = async () => {
    if (!onAirportsUpdate) {
      debugLog('⚠️ Cannot send airport request - missing onAirportsUpdate callback');
      return;
    }

    const bounds = map.getBounds();
    const token = getAccessToken();

    if (!token) {
      console.warn('⚠️ No access token available for airport request');
      return;
    }

    const requestData = {
      topLat: bounds.getNorth(),
      bottomLat: bounds.getSouth(),
      leftLon: bounds.getWest(),
      rightLon: bounds.getEast()
    };

    debugLog('✈️ Sending airport HTTP request for bounds:', requestData);

    try {
      const response = await getAirportsInBounds(requestData, token);

      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        debugLog('✅ Received airports:', response.airports?.length || 0);
        if (onAirportsUpdate && response.airports) {
          onAirportsUpdate(response.airports);
        }
      } else {
        console.error('❌ Failed to get airports:', response.header?.errors);
      }
    } catch (error) {
      console.error('❌ Error fetching airports:', error);
    }
  };

  const debouncedSendAirportRequest = () => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for 3 seconds
    debounceTimerRef.current = setTimeout(() => {
      sendAirportRequest();
    }, 3000);
  };

  // Listen to map events
  useMapEvents({
    moveend: () => {
      debouncedSendAirportRequest();
    },
    zoomend: () => {
      debouncedSendAirportRequest();
    },
    resize: () => {
      debouncedSendAirportRequest();
    },
  });

  // Send initial request when map is ready
  useEffect(() => {
    if (map && onAirportsUpdate) {
      // Send initial request after a short delay to ensure map is fully loaded
      const timer = setTimeout(() => {
        sendAirportRequest();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [map, onAirportsUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return null;
}

// Component to apply CSS filter to tile layer
function TileFilterController({ filter }) {
  const map = useMap();

  useEffect(() => {
    // Apply filter to the tile pane
    const tilePane = map.getPane('tilePane');
    if (tilePane) {
      tilePane.style.filter = filter;
    }
  }, [map, filter]);

  return null;
}

// Component to create custom pane for furthest distance markers (lower z-index than aircraft)
function FurthestDistancePaneController() {
  const map = useMap();

  useEffect(() => {
    // Create a custom pane for furthest distance markers with lower z-index
    // Default panes: tile=200, overlay=400, shadow=500, marker=600, tooltip=650, popup=700
    // We want furthest distance markers below aircraft markers (600), so use 550
    // Using 550 keeps them below markerPane (600) but above shadowPane (500)
    if (!map.getPane('furthestDistancePane')) {
      map.createPane('furthestDistancePane');
      const pane = map.getPane('furthestDistancePane');
      pane.style.zIndex = 550;
      // Ensure pointer events pass through to markers inside the pane
      pane.style.pointerEvents = 'none';
    }
  }, [map]);

  return null;
}

// Component to create custom pane for airport features (below markers, above tiles)
function AirportFeaturesPaneController() {
  const map = useMap();

  useEffect(() => {
    // Create a custom pane for airport features with z-index below markers but above tiles
    // Default panes: tile=200, overlay=400, shadow=500, marker=600
    // Using 450 keeps airport features below shadowPane (500) but above overlay (400)
    if (!map.getPane('airportFeaturesPane')) {
      map.createPane('airportFeaturesPane');
      const pane = map.getPane('airportFeaturesPane');
      pane.style.zIndex = 450;
      // Allow pointer events for tooltips on airport features
      pane.style.pointerEvents = 'auto';
    }
  }, [map]);

  return null;
}

// Component to create custom pane for NOTAM overlays (below airport features)
function NotamPaneController() {
  const map = useMap();

  useEffect(() => {
    if (!map.getPane('notamPane')) {
      map.createPane('notamPane');
      const pane = map.getPane('notamPane');
      pane.style.zIndex = 620;  // Above marker pane (600) so NOTAM clicks aren't blocked
      pane.style.pointerEvents = 'none';  // Pane itself transparent to clicks
    }
  }, [map]);

  return null;
}

// Component to save zoom level and center position, and track current zoom
function ZoomHandler({ onZoomChange }) {
  const map = useMap();

  useMapEvents({
    zoomend: () => {
      const zoom = map.getZoom();
      localStorage.setItem('mapZoom', zoom.toString());
      if (onZoomChange) {
        onZoomChange(zoom);
      }
    },
    moveend: () => {
      const center = map.getCenter();
      localStorage.setItem('mapCenter', JSON.stringify([center.lat, center.lng]));
    },
  });

  // Report initial zoom level
  React.useEffect(() => {
    if (onZoomChange) {
      onZoomChange(map.getZoom());
    }
  }, [map, onZoomChange]);

  return null;
}

// Detects USER-initiated zoom gestures (pinch, wheel, double-click, +/- keys
// or the zoom control) and switches auto-zoom off, so the map stops fighting
// the user's fingers. Programmatic zooms (fitBounds from AutoZoomController)
// don't come through these input events, so they never trip it.
function ManualZoomDetector({ autoZoom, onAutoZoomChange }) {
  const map = useMap();
  const autoZoomRef = React.useRef(autoZoom);
  autoZoomRef.current = autoZoom;
  const onChangeRef = React.useRef(onAutoZoomChange);
  onChangeRef.current = onAutoZoomChange;

  React.useEffect(() => {
    if (!map) return;
    const container = map.getContainer();

    const disableAutoZoom = () => {
      if (autoZoomRef.current && autoZoomRef.current !== 'none' && onChangeRef.current) {
        onChangeRef.current('none');
      }
    };

    // Wheel zoom (desktop) + pinch (two-finger touch) + double-click zoom.
    const onWheel = () => disableAutoZoom();
    const onTouchStart = (e) => {
      if (e.touches && e.touches.length >= 2) disableAutoZoom();
    };
    const onDblClick = () => disableAutoZoom();

    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('dblclick', onDblClick);

    return () => {
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('dblclick', onDblClick);
    };
  }, [map]);

  return null;
}

// Fits the map to the selected aircraft's track when the "zoom when aircraft
// selected" option is on, and restores the previous view on deselect.
// Enforces a 30 km minimum span so a short track (or a single position, before
// the track has loaded) doesn't slam the map to maximum zoom.
function SelectionZoomController({ enabled, selectedAircraftHex, trackData, aircraftData }) {
  const map = useMap();
  const prevViewRef = React.useRef(null);   // view to restore on deselect
  const fittedKeyRef = React.useRef(null);  // what we've already fitted to

  React.useEffect(() => {
    if (!map) return;

    // Deselected, or the option is off: put the map back where it was.
    if (!enabled || !selectedAircraftHex) {
      if (fittedKeyRef.current && prevViewRef.current) {
        map.setView(prevViewRef.current.center, prevViewRef.current.zoom, { animate: true });
      }
      fittedKeyRef.current = null;
      prevViewRef.current = null;
      return;
    }

    const points = [];
    (trackData?.points || []).forEach(p => {
      if (p.latitude != null && p.longitude != null) points.push([p.latitude, p.longitude]);
    });
    const live = aircraftData?.[selectedAircraftHex];
    if (live?.lat != null && live?.lon != null) points.push([live.lat, live.lon]);
    if (points.length === 0) return; // nothing to fit yet — wait for data

    // Fit once for the position, then once more when the track arrives (it is
    // fetched asynchronously after selection). After that, leave the view alone
    // so the user can pan and zoom around the selection freely.
    const key = `${selectedAircraftHex}|${(trackData?.points?.length || 0) > 0 ? 'track' : 'pos'}`;
    if (fittedKeyRef.current === key) return;

    // Remember the pre-selection view the first time we move the map.
    if (!prevViewRef.current) {
      prevViewRef.current = { center: map.getCenter(), zoom: map.getZoom() };
    }

    // Bounds computed by hand — Map.js imports react-leaflet, not the Leaflet
    // `L` object, and fitBounds accepts a plain [[s,w],[n,e]] array.
    let south = Infinity, north = -Infinity, west = Infinity, east = -Infinity;
    points.forEach(([lat, lon]) => {
      if (lat < south) south = lat;
      if (lat > north) north = lat;
      if (lon < west) west = lon;
      if (lon > east) east = lon;
    });

    const MIN_SPAN_KM = 30;
    const centreLat = (south + north) / 2;
    const centreLon = (west + east) / 2;
    const halfLat = (MIN_SPAN_KM / 2) / 111.32;
    // Guard the cosine so extreme latitudes can't blow the longitude span up.
    const halfLon = halfLat / Math.max(0.2, Math.cos(centreLat * Math.PI / 180));

    const bounds = [
      [Math.min(south, centreLat - halfLat), Math.min(west, centreLon - halfLon)],
      [Math.max(north, centreLat + halfLat), Math.max(east, centreLon + halfLon)]
    ];

    map.fitBounds(bounds, { padding: [40, 40], animate: true });
    fittedKeyRef.current = key;
  }, [map, enabled, selectedAircraftHex, trackData, aircraftData]);

  return null;
}

// Component to center map on selected receiver: on page load as well as on every change, so
// the map opens over the receiver being watched rather than wherever it was last panned to.
function ReceiverCenterController({ selectedReceiver, receivers }) {
  const map = useMap();
  // The selection last centred on; null until the first one resolves.
  const centredOnRef = useRef(null);

  useEffect(() => {
    if (centredOnRef.current === selectedReceiver) return;

    // "All Receivers" has no single place to centre on; keep the saved view.
    if (selectedReceiver === 'All Receivers') {
      centredOnRef.current = selectedReceiver;
      return;
    }

    // Receivers load after the map mounts. Until this one is in the list, leave the
    // ref alone so the effect runs again when it arrives.
    const receiver = receivers.find(r => r.receiverName === selectedReceiver);
    if (!receiver || !receiver.latitude || !receiver.longitude) return;

    const centre = [receiver.latitude, receiver.longitude];
    debugLog(`📍 Centering map on receiver: ${receiver.receiverName} at [${centre}]`);
    if (centredOnRef.current === null) {
      map.setView(centre, 10); // first load: no animation from the stale saved view
    } else {
      map.flyTo(centre, 10, { duration: 1.5 });
    }
    centredOnRef.current = selectedReceiver;
  }, [selectedReceiver, receivers, map]);

  return null;
}

// Component to handle map resize and re-zoom
function ResizeController({ resizeTrigger, autoZoom }) {
  const map = useMap();
  const lastTriggerRef = useRef(resizeTrigger);

  useEffect(() => {
    if (resizeTrigger !== lastTriggerRef.current) {
      lastTriggerRef.current = resizeTrigger;
      // Invalidate map size after container resize
      setTimeout(() => {
        map.invalidateSize();
        // If auto-zoom is enabled, trigger a re-zoom after resize
        if (autoZoom && autoZoom !== 'none') {
          // Small delay to let invalidateSize complete
          setTimeout(() => {
            map.invalidateSize();
          }, 100);
        }
      }, 50);
    }
  }, [map, resizeTrigger, autoZoom]);

  return null;
}

// Component to handle auto-zoom functionality
function AutoZoomController({ aircraftData, furthestDistanceData, autoZoom, selectedReceiver, receivers, targetAirport, airportMode, selectedAirport, airportFeaturesData, showParkingPositions }) {
  const map = useMap();
  const lastBoundsRef = useRef(null);
  const lastAutoZoomModeRef = useRef(autoZoom);
  const lastSelectedReceiverRef = useRef(selectedReceiver);
  const hasInitialZoomedRef = useRef(false);
  const receiverChangeDelayRef = useRef(null);
  const periodicZoomIntervalRef = useRef(null);
  const lastFurthestDataRef = useRef(null);
  const lastZoomTimeRef = useRef(0); // Timestamp of last zoom to prevent rapid re-zooming
  const isReceiverChangePendingRef = useRef(false); // Block zooms during receiver transition

  // Use refs to store current values to avoid stale closures
  const aircraftDataRef = useRef(aircraftData);
  const furthestDistanceDataRef = useRef(furthestDistanceData);
  const selectedReceiverRef = useRef(selectedReceiver);
  const receiversRef = useRef(receivers);
  const autoZoomRef = useRef(autoZoom);
  const targetAirportRef = useRef(targetAirport);

  // Keep refs in sync with props
  useEffect(() => {
    aircraftDataRef.current = aircraftData;
  }, [aircraftData]);

  useEffect(() => {
    furthestDistanceDataRef.current = furthestDistanceData;
  }, [furthestDistanceData]);

  useEffect(() => {
    selectedReceiverRef.current = selectedReceiver;
  }, [selectedReceiver]);

  useEffect(() => {
    receiversRef.current = receivers;
  }, [receivers]);

  useEffect(() => {
    autoZoomRef.current = autoZoom;
  }, [autoZoom]);

  useEffect(() => {
    targetAirportRef.current = targetAirport;
  }, [targetAirport]);

  // Refs for airport mode props
  const airportModeRef = useRef(airportMode);
  const selectedAirportRef = useRef(selectedAirport);
  const airportFeaturesDataRef = useRef(airportFeaturesData);
  const showParkingPositionsRef = useRef(showParkingPositions);

  useEffect(() => {
    airportModeRef.current = airportMode;
  }, [airportMode]);

  useEffect(() => {
    selectedAirportRef.current = selectedAirport;
  }, [selectedAirport]);

  useEffect(() => {
    airportFeaturesDataRef.current = airportFeaturesData;
  }, [airportFeaturesData]);

  useEffect(() => {
    showParkingPositionsRef.current = showParkingPositions;
  }, [showParkingPositions]);

  // Constants
  const BUFFER_MULTIPLIER = 1.10; // 10% buffer around bounds
  const RECEIVER_CHANGE_DELAY = 2000; // 2 seconds to wait for aircraft data after receiver change
  const PERIODIC_ZOOM_INTERVAL = 15000; // 15 seconds
  const MIN_ZOOM_INTERVAL = 5000; // Minimum 5 seconds between zooms to prevent jumping
  const MIN_RADIUS_KM = 100; // Minimum 100km radius when zooming to aircraft
  const KM_TO_DEG_LAT = 1 / 111; // Approximate km to degrees latitude

  // Main zoom function - adjusts map to fit all relevant data
  // Uses refs to always get current values and avoid stale closures
  const performZoom = React.useCallback((reason = 'update', forReceiver = null) => {
    const currentAutoZoom = autoZoomRef.current;
    const currentSelectedReceiver = selectedReceiverRef.current;
    const currentAircraftData = aircraftDataRef.current;
    const currentFurthestDistanceData = furthestDistanceDataRef.current;
    const currentReceivers = receiversRef.current;

    if (currentAutoZoom === 'none') return;

    // Block non-receiver-change zooms while a receiver change is pending
    // This prevents stale data from causing map jumps
    if (isReceiverChangePendingRef.current && reason !== 'receiver-change') {
      debugLog(`🔍 AutoZoom blocked (reason: ${reason}) - receiver change pending`);
      return;
    }

    // For receiver-change zooms, verify we're zooming for the current receiver
    if (reason === 'receiver-change' && forReceiver !== null && forReceiver !== currentSelectedReceiver) {
      debugLog(`🔍 AutoZoom skipping stale receiver-change zoom (was for: ${forReceiver}, current: ${currentSelectedReceiver})`);
      return;
    }

    // Skip non-essential zooms if we zoomed recently (prevent jumping)
    const now = Date.now();
    const timeSinceLastZoom = now - lastZoomTimeRef.current;
    const isEssentialZoom = reason === 'initial' || reason === 'mode-change' || reason === 'receiver-change';

    if (!isEssentialZoom && timeSinceLastZoom < MIN_ZOOM_INTERVAL) {
      debugLog(`🔍 AutoZoom skipping zoom (reason: ${reason}) - only ${timeSinceLastZoom}ms since last zoom`);
      return;
    }

    debugLog(`🔍 AutoZoom performing zoom (reason: ${reason}, mode: ${currentAutoZoom}, receiver: ${currentSelectedReceiver})`);
    lastZoomTimeRef.current = now;

    if (currentAutoZoom === 'aircraft') {
      const aircraft = Object.values(currentAircraftData || {});
      if (aircraft.length === 0) {
        debugLog(`🔍 AutoZoom: No aircraft data available`);
        return;
      }

      // Calculate bounds of all aircraft with positions
      const lats = aircraft.map(a => a.lat).filter(lat => lat != null);
      const lons = aircraft.map(a => a.lon).filter(lon => lon != null);

      if (lats.length === 0 || lons.length === 0) {
        debugLog(`🔍 AutoZoom: No aircraft with valid positions`);
        return;
      }

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);

      // Calculate center point for minimum radius enforcement
      const centerLat = (minLat + maxLat) / 2;
      const centerLon = (minLon + maxLon) / 2;

      // Calculate current range
      let latRange = maxLat - minLat;
      let lonRange = maxLon - minLon;

      // Enforce minimum 100km radius (200km diameter)
      const minLatRange = MIN_RADIUS_KM * 2 * KM_TO_DEG_LAT;
      // Longitude degrees vary with latitude, so adjust for current latitude
      const kmToDegreeLon = 1 / (111 * Math.cos(centerLat * Math.PI / 180));
      const minLonRange = MIN_RADIUS_KM * 2 * kmToDegreeLon;

      // Use the larger of actual range or minimum range
      latRange = Math.max(latRange, minLatRange);
      lonRange = Math.max(lonRange, minLonRange);

      // Add buffer
      const bufferedBounds = [
        [centerLat - latRange * BUFFER_MULTIPLIER / 2, centerLon - lonRange * BUFFER_MULTIPLIER / 2],
        [centerLat + latRange * BUFFER_MULTIPLIER / 2, centerLon + lonRange * BUFFER_MULTIPLIER / 2]
      ];

      debugLog(`🔍 AutoZoom: Flying to bounds with ${lats.length} aircraft (min radius: ${MIN_RADIUS_KM}km)`);
      map.flyToBounds(bufferedBounds, { duration: 1, padding: [20, 20] });
      lastBoundsRef.current = bufferedBounds;
      hasInitialZoomedRef.current = true;

    } else if (currentAutoZoom === 'range' && currentFurthestDistanceData && typeof currentFurthestDistanceData === 'object') {
      // Create a mapping from receiverUuid to receiverName
      const receiverUuidToName = {};
      (currentReceivers || []).forEach(receiver => {
        receiverUuidToName[receiver.receiverUuid] = receiver.receiverName;
      });

      // Collect records from selected receiver(s)
      const allRecords = [];
      Object.entries(currentFurthestDistanceData).forEach(([receiverUuid, receiverData]) => {
        const receiverName = receiverUuidToName[receiverUuid] || receiverUuid;

        // Filter by selected receiver
        if (currentSelectedReceiver === 'All Receivers' || receiverName === currentSelectedReceiver) {
          if (receiverData.bearingRecords && receiverData.bearingRecords.length > 0) {
            allRecords.push(...receiverData.bearingRecords);
          }
        }
      });

      if (allRecords.length === 0) {
        debugLog(`🔍 AutoZoom: No range records for receiver ${currentSelectedReceiver}`);
        return;
      }

      // Calculate bounds of range outline
      const lats = allRecords.map(r => r.latitude);
      const lons = allRecords.map(r => r.longitude);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);

      // No buffer - use exact bounds
      const bufferedBounds = [
        [minLat, minLon],
        [maxLat, maxLon]
      ];

      debugLog(`🔍 AutoZoom: Flying to range bounds with ${allRecords.length} records`);
      map.flyToBounds(bufferedBounds, { duration: 1, padding: [0, 0] });
      lastBoundsRef.current = bufferedBounds;
      hasInitialZoomedRef.current = true;

    } else if (currentAutoZoom === 'airport') {
      // Use selectedAirport from props if in airport mode, otherwise use internal targetAirport
      const currentTargetAirport = airportModeRef.current && selectedAirportRef.current
        ? selectedAirportRef.current
        : targetAirportRef.current;

      if (!currentTargetAirport) {
        debugLog(`🔍 AutoZoom: No target airport available`);
        return;
      }

      // Check if we have airport bounds
      if (currentTargetAirport.bounds) {
        const { north, south, east, west } = currentTargetAirport.bounds;
        const airportBounds = [
          [south, west],
          [north, east]
        ];

        debugLog(`🔍 AutoZoom: Flying to airport ${currentTargetAirport.icao || currentTargetAirport.icaoCode} bounds`);
        map.flyToBounds(airportBounds, { duration: 1, padding: [50, 50], maxZoom: 16 });
        lastBoundsRef.current = airportBounds;
        hasInitialZoomedRef.current = true;
      } else if (currentTargetAirport.latitude && currentTargetAirport.longitude) {
        // Fall back to centering on airport location with ~2km radius
        // 2km ≈ 0.018 degrees latitude
        const KM_RADIUS = 2;
        const latOffset = KM_RADIUS / 111;
        const lonOffset = KM_RADIUS / (111 * Math.cos(currentTargetAirport.latitude * Math.PI / 180));

        const airportBounds = [
          [currentTargetAirport.latitude - latOffset, currentTargetAirport.longitude - lonOffset],
          [currentTargetAirport.latitude + latOffset, currentTargetAirport.longitude + lonOffset]
        ];

        debugLog(`🔍 AutoZoom: Flying to airport ${currentTargetAirport.icao || currentTargetAirport.icaoCode} location (no bounds available)`);
        map.flyToBounds(airportBounds, { duration: 1, padding: [50, 50], maxZoom: 16 });
        lastBoundsRef.current = airportBounds;
        hasInitialZoomedRef.current = true;
      }
    } else if (currentAutoZoom === 'stands') {
      // Stands zoom mode - zoom to parking positions bounds
      const currentFeatures = airportFeaturesDataRef.current;
      const currentTargetAirport = airportModeRef.current && selectedAirportRef.current
        ? selectedAirportRef.current
        : targetAirportRef.current;

      const parkingPositions = currentFeatures?.parkingPositions || [];

      if (parkingPositions.length > 0) {
        // Get all lat/lon values from parking positions
        const lats = parkingPositions
          .filter(p => p.latitude != null && p.longitude != null)
          .map(p => p.latitude);
        const lons = parkingPositions
          .filter(p => p.latitude != null && p.longitude != null)
          .map(p => p.longitude);

        if (lats.length > 0 && lons.length > 0) {
          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);
          const minLon = Math.min(...lons);
          const maxLon = Math.max(...lons);

          // Add 10% padding to the bounds
          const latPadding = (maxLat - minLat) * 0.1 || 0.001;
          const lonPadding = (maxLon - minLon) * 0.1 || 0.001;

          const standsBounds = [
            [minLat - latPadding, minLon - lonPadding],
            [maxLat + latPadding, maxLon + lonPadding]
          ];

          debugLog(`🔍 AutoZoom: Flying to parking positions bounds (${parkingPositions.length} positions)`);
          map.flyToBounds(standsBounds, { duration: 1, padding: [30, 30] });
          lastBoundsRef.current = standsBounds;
          hasInitialZoomedRef.current = true;
          return;
        }
      }

      // Fallback: zoom to airport center at higher zoom level if no parking positions
      if (currentTargetAirport?.latitude && currentTargetAirport?.longitude) {
        debugLog(`🔍 AutoZoom: Flying to airport center (no parking positions found)`);
        map.flyTo([currentTargetAirport.latitude, currentTargetAirport.longitude], 17, { duration: 1 });
        hasInitialZoomedRef.current = true;
      }
    }
  }, [map, KM_TO_DEG_LAT]); // Only depend on map and constants - use refs for everything else

  // Check if any aircraft is outside current map bounds
  const checkAircraftOutsideBounds = React.useCallback(() => {
    if (autoZoomRef.current !== 'aircraft') return false;

    const aircraft = Object.values(aircraftDataRef.current || {});
    if (aircraft.length === 0) return false;

    const currentBounds = map.getBounds();

    for (const ac of aircraft) {
      if (ac.lat != null && ac.lon != null) {
        if (!currentBounds.contains([ac.lat, ac.lon])) {
          debugLog(`🔍 Aircraft ${ac.hex} at [${ac.lat}, ${ac.lon}] is outside current bounds`);
          return true;
        }
      }
    }
    return false;
  }, [map]); // Only depend on map - use refs for data

  // Handle mode/receiver changes
  useEffect(() => {
    if (autoZoom === 'none') {
      // Clear any timers when auto zoom is disabled
      if (receiverChangeDelayRef.current) {
        clearTimeout(receiverChangeDelayRef.current);
        receiverChangeDelayRef.current = null;
      }
      if (periodicZoomIntervalRef.current) {
        clearInterval(periodicZoomIntervalRef.current);
        periodicZoomIntervalRef.current = null;
      }
      lastAutoZoomModeRef.current = 'none';
      hasInitialZoomedRef.current = false;
      isReceiverChangePendingRef.current = false;
      return;
    }

    // Check if mode or receiver changed
    const modeJustChanged = lastAutoZoomModeRef.current !== autoZoom;
    const receiverJustChanged = lastSelectedReceiverRef.current !== selectedReceiver;

    if (modeJustChanged || receiverJustChanged) {
      lastAutoZoomModeRef.current = autoZoom;
      hasInitialZoomedRef.current = false;

      // Clear any existing receiver change timer
      if (receiverChangeDelayRef.current) {
        clearTimeout(receiverChangeDelayRef.current);
        receiverChangeDelayRef.current = null;
      }

      // If receiver changed, schedule delayed zoom to allow data to arrive
      if (receiverJustChanged) {
        // Capture the new receiver for the delayed callback
        const targetReceiver = selectedReceiver;
        lastSelectedReceiverRef.current = selectedReceiver;

        // Block other zooms while waiting for receiver data
        isReceiverChangePendingRef.current = true;

        debugLog(`🔄 Receiver changed to ${targetReceiver}, blocking other zooms for ${RECEIVER_CHANGE_DELAY}ms`);
        receiverChangeDelayRef.current = setTimeout(() => {
          debugLog(`⏰ Receiver change delay complete, applying zoom for ${targetReceiver}`);
          receiverChangeDelayRef.current = null;
          isReceiverChangePendingRef.current = false;
          performZoom('receiver-change', targetReceiver);
        }, RECEIVER_CHANGE_DELAY);
      } else {
        // Mode changed - zoom immediately
        lastSelectedReceiverRef.current = selectedReceiver;
        performZoom('mode-change');
      }
    }

    // Set up 15-second periodic zoom interval
    if (!periodicZoomIntervalRef.current) {
      debugLog(`⏱️ Starting 15-second auto-zoom interval (mode: ${autoZoom})`);
      periodicZoomIntervalRef.current = setInterval(() => {
        if (autoZoom !== 'none') {
          performZoom('periodic');
        }
      }, PERIODIC_ZOOM_INTERVAL);
    }

    return () => {
      if (receiverChangeDelayRef.current) {
        clearTimeout(receiverChangeDelayRef.current);
        isReceiverChangePendingRef.current = false;
      }
    };
  }, [autoZoom, selectedReceiver, performZoom]);

  // Cleanup periodic interval on unmount or when autoZoom becomes 'none'
  useEffect(() => {
    return () => {
      if (periodicZoomIntervalRef.current) {
        clearInterval(periodicZoomIntervalRef.current);
        periodicZoomIntervalRef.current = null;
      }
    };
  }, []);

  // Check for aircraft outside bounds on every aircraft data update
  useEffect(() => {
    if (autoZoom !== 'aircraft') return;
    // Don't zoom while receiver change is pending - this prevents stale data from causing jumps
    if (isReceiverChangePendingRef.current) {
      debugLog('🔍 Skipping aircraft bounds check - receiver change pending');
      return;
    }

    if (!hasInitialZoomedRef.current) {
      // Initial zoom
      performZoom('initial');
      return;
    }

    // Check if any aircraft is outside current map bounds
    if (checkAircraftOutsideBounds()) {
      performZoom('aircraft-outside-bounds');
    }
  }, [autoZoom, aircraftData, performZoom, checkAircraftOutsideBounds]);

  // Check for new max distance data
  useEffect(() => {
    if (autoZoom !== 'range') return;
    // Don't zoom while receiver change is pending - this prevents stale data from causing jumps
    if (isReceiverChangePendingRef.current) {
      debugLog('🔍 Skipping range bounds check - receiver change pending');
      return;
    }

    // Serialize current data for comparison
    const currentDataStr = JSON.stringify(furthestDistanceData);

    if (!hasInitialZoomedRef.current) {
      // Initial zoom
      if (furthestDistanceData && Object.keys(furthestDistanceData).length > 0) {
        performZoom('initial');
        lastFurthestDataRef.current = currentDataStr;
      }
      return;
    }

    // Check if data changed
    if (currentDataStr !== lastFurthestDataRef.current) {
      debugLog('📍 New max distance data received, adjusting zoom');
      performZoom('new-max-distance');
      lastFurthestDataRef.current = currentDataStr;
    }
  }, [autoZoom, furthestDistanceData, performZoom]);

  // Handle airport zoom when targetAirport is available
  useEffect(() => {
    if (autoZoom !== 'airport' && autoZoom !== 'stands') return;
    // Don't zoom while receiver change is pending
    if (isReceiverChangePendingRef.current) {
      debugLog('🔍 Skipping airport zoom - receiver change pending');
      return;
    }

    // For airport mode, use selectedAirport prop; otherwise use internal targetAirport
    const effectiveTargetAirport = airportMode && selectedAirport ? selectedAirport : targetAirport;

    if (!hasInitialZoomedRef.current && effectiveTargetAirport) {
      // Initial zoom to airport or stands
      performZoom('initial');
    }
  }, [autoZoom, targetAirport, selectedAirport, airportMode, performZoom]);

  return null;
}

const Map = React.memo(function Map({
  aircraftData = {},
  receivers = [],
  selectedReceiver = 'All Receivers',
  airports = [],
  furthestDistanceData = null,
  allTimeFurthestDistanceData = null,
  aircraftTrack = null,
  selectedAircraftHex = null,
  onAircraftSelect,
  onAirportsUpdate,
  autoZoom = 'none',
  resizeTrigger = 0,
  // Aircraft filter (Live map) — shown inside the on-demand filter overlay.
  // When onAircraftFilterChange is absent (History/Airport use), the overlay
  // only offers the NOTAMs toggle.
  aircraftFilter = 'all',
  onAircraftFilterChange = null,
  // Top-right control stack (Live map). Each control renders only when its
  // callback is provided, so History/Airport maps stay unchanged.
  receiverOptions = null,          // [{receiverName, receiverUuid, status}] incl. viewer receivers
  showAllReceiversOption = false,  // offer the "All Receivers" entry
  onReceiverChange = null,         // (receiverName) => void
  onAutoZoomChange = null,         // (mode) => void
  isFullscreen = false,
  onToggleFullscreen = null,
  onShowList = null,               // switch to aircraft list (phone)
  listCount = 0,
  // Airport mode props
  airportMode = false,                  // Enable airport-specific features
  airportFeaturesData = null,           // Airport infrastructure data (from Airport.js)
  selectedAirport = null,               // Airport metadata for centering
  parkedAircraft = [],                  // Ground aircraft to show at parking positions
  showParkingPositions = false,         // Whether to show stand markers
  groundTrackPoints = null,             // Track polylines for selected ground operation
  inboundTrackPoints = null,            // [[lat,lon], ...] approach track for selected aircraft
  onParkedAircraftSelect = null,        // Callback for selecting parked aircraft
}) {
  // State for airport zoom mode
  const [airportFeatures, setAirportFeatures] = useState(null);
  const [targetAirport, setTargetAirport] = useState(null);
  const [, setAirportFeaturesLoading] = useState(false);

  const [position, setPosition] = useState(() => {
    // Load saved center position or default to London
    const savedCenter = localStorage.getItem('mapCenter');
    if (savedCenter) {
      try {
        return JSON.parse(savedCenter);
      } catch (e) {
        return [51.505, -0.09];
      }
    }
    return [51.505, -0.09];
  });
  const [loading, setLoading] = useState(true);
  const [zoom] = useState(() => {
    // Load zoom from localStorage or default to 8
    const savedZoom = localStorage.getItem('mapZoom');
    return savedZoom ? parseFloat(savedZoom) : 8;
  });
  const [currentZoom, setCurrentZoom] = useState(() => {
    const savedZoom = localStorage.getItem('mapZoom');
    return savedZoom ? parseFloat(savedZoom) : 8;
  });

  // State for selected range record (shown in popup when clicking range dots)
  const [selectedRangeRecord, setSelectedRangeRecord] = useState(null);
  const [rangeRecordTrackData, setRangeRecordTrackData] = useState(null);
  // Sequence counter so a slow response from a previously-clicked range dot
  // can't overwrite the current selection — same staleness fix as the
  // selected-aircraft path in App.js.
  const rangeRecordSeqRef = useRef(0);

  // Fetch full track data when a range record is selected
  useEffect(() => {
    if (!selectedRangeRecord) {
      // Bump on close too so an in-flight response gets discarded
      rangeRecordSeqRef.current++;
      setRangeRecordTrackData(null);
      return;
    }

    const mySeq = ++rangeRecordSeqRef.current;

    const fetchTrackData = async () => {
      // Get receiver UUID from selected receiver
      const receiverUuid = receivers.find(r => r.receiverName === selectedReceiver)?.receiverUuid;
      // WebSocket uses icaoAddress, HTTP API uses icao24
      const icao24 = selectedRangeRecord.icao24 || selectedRangeRecord.icaoAddress;

      if (!receiverUuid || !icao24) {
        debugLog('Cannot fetch track data: missing receiverUuid or icao24', { receiverUuid, icao24, selectedRangeRecord });
        return;
      }

      try {
        const token = getAccessToken();
        if (!token) return;

        // Get the date from the record timestamp, or use today
        const recordDate = selectedRangeRecord.timestamp
          ? new Date(selectedRangeRecord.timestamp).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];

        debugLog('Fetching tracks for range record:', { icao24, receiverUuid, recordDate });

        // Fetch aircraft tracks to get trackId
        const tracksResponse = await getAircraftTracks(icao24, receiverUuid, recordDate, token);

        debugLog('Tracks response:', tracksResponse);

        if ((tracksResponse.header?.apiStatus === 'OK' || tracksResponse.header?.apiStatus === 'SUCCESS') && tracksResponse.tracks?.length > 0) {
          // The track that SET this record: the latest one starting at or
          // before the record's time. Not an array position — the backend
          // returns newest-first, so tracks[length-1] was the day's OLDEST
          // track and the popup showed an earlier flight's callsign.
          const latestTrack = pickTrackForRecord(tracksResponse.tracks, selectedRangeRecord.timestamp);

          debugLog('Fetching track points for trackId:', latestTrack.trackId);

          // Fetch full track points with metadata
          const trackResponse = await getTrackPoints(latestTrack.trackId, 5, token);

          debugLog('Track points response:', trackResponse);
          debugLog('Track response has photo:', !!trackResponse.photo);
          debugLog('Track response has country:', !!trackResponse.country);

          if (trackResponse.header?.apiStatus === 'OK' || trackResponse.header?.apiStatus === 'SUCCESS') {
            // Drop stale response if user has clicked another dot or closed the popup
            if (mySeq !== rangeRecordSeqRef.current) {
              debugLog('⏭️ Discarding stale range-record response (seq mismatch)');
              return;
            }
            // Merge range record with track data (track data has photo, country, etc.)
            setRangeRecordTrackData({
              ...trackResponse,
              // Keep range-specific fields from the original record
              distance: selectedRangeRecord.distance ?? selectedRangeRecord.distanceKm,
              bearing: selectedRangeRecord.bearing,
              compassDirection: selectedRangeRecord.compassDirection,
              rangeTimestamp: selectedRangeRecord.timestamp
            });
          } else {
            debugLog('Track points request failed:', trackResponse.header?.errors);
          }
        } else {
          debugLog('No tracks found for aircraft:', { icao24, recordDate, tracksResponse });
        }
      } catch (error) {
        debugLog('Error fetching track data for range record:', error);
      }
    };

    fetchTrackData();
  }, [selectedRangeRecord, receivers, selectedReceiver]);

  // Get map style settings from context (single interval at app level)
  const mapStyleSettings = useMapSettings();

  // Get colour settings from context (reactive to theme changes)
  const colourSettings = useColourSettings();

  // Toggle any boolean overlay setting (NOTAMs, range rings, range outlines).
  // Settings live in the shared colour-settings store, so the change persists
  // and every consumer (Map, ReceiverMarker, History) picks it up reactively.
  // defaultValue matters for keys that default OFF (zoomOnAircraftSelect): a
  // plain `current[key] === false` flip would leave an undefined value stuck
  // off on the first click.
  const toggleOverlaySetting = useCallback((key, defaultValue = true) => {
    const current = getColourSettings();
    const currentValue = current[key] !== undefined ? current[key] : defaultValue;
    saveColourSettings({ ...current, [key]: !currentValue });
  }, []);

  const handleNotamToggle = useCallback(() => {
    toggleOverlaySetting('showNotams');
  }, [toggleOverlaySetting]);

  // Overlay toggles offered in the filter panel, in draw order on the map.
  const overlayToggles = [
    ['showRangeRings', 'Range rings'],
    ['showRangeOutline', 'Daily max range'],
    ['showAllTimeRangeOutline', 'All-time max range'],
    ['showNotams', 'NOTAMs'],
  ];

  // Top-right control stack: one panel open at a time
  // (null | 'filter' | 'receiver' | 'zoom').
  const [openPanel, setOpenPanel] = useState(null);

  const togglePanel = useCallback((id) => {
    setOpenPanel(prev => (prev === id ? null : id));
  }, []);
  const filterOptions = [
    ['all', 'All aircraft'],
    ['new', 'New only'],
    ['notRegular', 'Interesting'],
    ['military', 'Military'],
    // Settings → "Ground vehicles: Hidden" removes the option (and App.js drops the data)
    ...(colourSettings.showGroundVehicles !== false ? [['groundVehicles', 'Ground vehicles']] : []),
  ];
  const zoomOptions = [
    ['none', 'No auto zoom'],
    ['aircraft', 'Visible aircraft'],
    ['range', 'Range'],
  ];

  // True while an aircraft is selected AND the zoom-on-select option is on —
  // auto-zoom yields to the selection zoom for the duration.
  const selectionZoomActive = colourSettings.zoomOnAircraftSelect === true && !!selectedAircraftHex;

  // Get current tile layer config
  const tileLayerConfig = useMemo(() => {
    const styleConfig = MAP_STYLES[mapStyleSettings.style] || MAP_STYLES.openstreetmap;
    return {
      url: styleConfig.url,
      attribution: styleConfig.attribution,
      filter: getMapFilter(mapStyleSettings.brightness)
    };
  }, [mapStyleSettings]);

  // Callback to handle zoom changes from ZoomHandler
  const handleZoomChange = React.useCallback((newZoom) => {
    setCurrentZoom(newZoom);
  }, []);

  useEffect(() => {
    // Only use geolocation if no saved position exists
    const savedCenter = localStorage.getItem('mapCenter');
    if (!savedCenter && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPosition([pos.coords.latitude, pos.coords.longitude]);
          setLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLoading(false);
        }
      );
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch airport features when airport zoom mode is enabled
  useEffect(() => {
    if (autoZoom !== 'airport') {
      // Clear airport features when not in airport mode
      setAirportFeatures(null);
      setTargetAirport(null);
      return;
    }

    // Find the selected receiver
    const receiver = receivers.find(r => r.receiverName === selectedReceiver);
    if (!receiver && selectedReceiver !== 'All Receivers') {
      debugLog('🛬 Airport zoom: No receiver found for', selectedReceiver);
      return;
    }

    // If "All Receivers" is selected, use the first receiver
    const targetReceiver = receiver || receivers[0];
    if (!targetReceiver) {
      debugLog('🛬 Airport zoom: No receivers available');
      return;
    }

    const fetchAirportData = async () => {
      const token = getAccessToken();
      if (!token) {
        debugLog('🛬 Airport zoom: No access token');
        return;
      }

      setAirportFeaturesLoading(true);

      try {
        let airportIcao = targetReceiver.airportIcao;

        // If receiver doesn't have airportIcao set, find nearest airport
        if (!airportIcao) {
          debugLog('🛬 Airport zoom: No airportIcao set, finding nearest airport');
          const nearestResponse = await getNearestAirport(
            targetReceiver.latitude,
            targetReceiver.longitude,
            token
          );

          if ((nearestResponse.header?.apiStatus === 'OK' || nearestResponse.header?.apiStatus === 'SUCCESS') && nearestResponse.airport) {
            airportIcao = nearestResponse.airport.icao;
            setTargetAirport(nearestResponse.airport);
            debugLog('🛬 Airport zoom: Found nearest airport', nearestResponse.airport);
          } else {
            debugLog('🛬 Airport zoom: Could not find nearest airport', nearestResponse);
            setAirportFeaturesLoading(false);
            return;
          }
        }

        // Fetch airport features
        debugLog('🛬 Airport zoom: Fetching features for', airportIcao);
        const featuresResponse = await getAirportFeatures(airportIcao, token);

        if ((featuresResponse.header?.apiStatus === 'OK' || featuresResponse.header?.apiStatus === 'SUCCESS') && featuresResponse.features) {
          debugLog('🛬 Airport zoom: Got features', {
            runways: featuresResponse.features.runways?.length || 0,
            taxiways: featuresResponse.features.taxiways?.length || 0,
            aprons: featuresResponse.features.aprons?.length || 0
          });
          setAirportFeatures(featuresResponse.features);

          // Set targetAirport with bounds if available (needed for zoom)
          if (featuresResponse.airport) {
            setTargetAirport(featuresResponse.airport);
          } else if (!targetAirport) {
            // Fallback: create basic airport info from receiver's airportIcao
            setTargetAirport({
              icao: airportIcao,
              latitude: targetReceiver.latitude,
              longitude: targetReceiver.longitude
            });
          }
        } else {
          debugLog('🛬 Airport zoom: Failed to get features', featuresResponse);
        }
      } catch (error) {
        debugLog('🛬 Airport zoom: Error fetching airport data', error);
      } finally {
        setAirportFeaturesLoading(false);
      }
    };

    fetchAirportData();
  }, [autoZoom, selectedReceiver, receivers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug: Log airports prop in Map component (disabled for performance)
  // useEffect(() => {
  //   if (airports.length > 0) {
  //     debugLog('🗺️ Map component - airports prop:', airports);
  //   }
  // }, [airports]);

  // Create airport markers - memoized to prevent recreation on every render
  const airportMarkers = useMemo(() => {
    return airports.map((airport) => {
      return <AirportMarker key={airport.id || airport.airportId || airport.icao} airport={airport} />;
    });
  }, [airports]);

  // Create furthest distance markers and polylines - memoized to prevent recreation on every render
  const { furthestDistanceMarkers, furthestDistancePolylines } = useMemo(() => {
    let markers = [];
    let polylines = [];

    if (furthestDistanceData && typeof furthestDistanceData === 'object') {
      // Create a mapping from receiverUuid to receiverName
      const receiverUuidToName = {};
      receivers.forEach(receiver => {
        receiverUuidToName[receiver.receiverUuid] = receiver.receiverName;
      });

      // Helper function to select markers to display
      const selectMarkersToDisplay = (records) => {
        // Get top 10 furthest overall
        const sortedByDistance = [...records].sort((a, b) => recordDistanceKm(b) - recordDistanceKm(a));
        const top10 = sortedByDistance.slice(0, 10);
        const top10Set = new Set(top10.map(r => `${r.bearing}-${r.latitude}-${r.longitude}`));

        // Group by 10-degree segments (0-360 degrees)
        const segmentSize = 10;
        const segments = {};

        records.forEach(record => {
          const segmentIndex = Math.floor(record.bearing / segmentSize);
          if (!segments[segmentIndex]) {
            segments[segmentIndex] = [];
          }
          segments[segmentIndex].push(record);
        });

        // Get furthest in each segment
        const segmentFurthest = [];
        Object.values(segments).forEach(segmentRecords => {
          if (segmentRecords.length > 0) {
            // Find furthest in this segment
            const furthest = segmentRecords.reduce((max, r) =>
              recordDistanceKm(r) > recordDistanceKm(max) ? r : max
            );

            // Add if not already in top 10
            const key = `${furthest.bearing}-${furthest.latitude}-${furthest.longitude}`;
            if (!top10Set.has(key)) {
              segmentFurthest.push(furthest);
            }
          }
        });

        // Combine top 10 and segment furthest
        return [...top10, ...segmentFurthest];
      };

      // Skip if range outline is disabled
      if (colourSettings.showRangeOutline === false) {
        return { furthestDistanceMarkers: markers, furthestDistancePolylines: polylines };
      }

      // Iterate through each receiver's data
      Object.entries(furthestDistanceData).forEach(([receiverUuid, receiverData], index) => {
        // Get receiver name from UUID
        const receiverName = receiverUuidToName[receiverUuid] || receiverUuid;

        // Skip if not selected (when a specific receiver is selected)
        if (selectedReceiver !== 'All Receivers' && receiverName !== selectedReceiver) {
          return;
        }

        if (!receiverData.bearingRecords || receiverData.bearingRecords.length === 0) {
          return;
        }

        // Sort records by bearing to ensure proper line connection
        const sortedRecords = [...receiverData.bearingRecords].sort((a, b) => a.bearing - b.bearing);

        // Select markers to display
        const markersToDisplay = selectMarkersToDisplay(sortedRecords);

        // Create markers for this receiver
        const receiverMarkers = markersToDisplay.map((record) => (
          <FurthestDistanceMarker
            key={`${receiverUuid}-${record.bearing}-${record.latitude.toFixed(6)}-${record.longitude.toFixed(6)}`}
            record={record}
            colourSettings={colourSettings}
            onSelect={setSelectedRangeRecord}
          />
        ));
        markers.push(...receiverMarkers);

        // Create polyline for this receiver
        const positions = sortedRecords.map(record => [record.latitude, record.longitude]);
        // Close the loop by adding the first point at the end
        if (positions.length > 0) {
          positions.push(positions[0]);
        }

        const polyline = (
          <Polyline
            key={`polyline-${receiverUuid}`}
            positions={positions}
            pathOptions={{
              color: colourSettings.maxDistanceOutline,
              weight: 2,
              opacity: 0.5,
              interactive: false
            }}
          />
        );
        polylines.push(polyline);
      });
    }

    return { furthestDistanceMarkers: markers, furthestDistancePolylines: polylines };
  }, [furthestDistanceData, selectedReceiver, receivers, colourSettings]);

  // Create all-time furthest distance polylines AND record dots.
  // The dots are clickable and open the same range-record popup as the daily
  // ones, exposing the aircraft (hex/callsign/registration/type/altitude/time)
  // that set each all-time record — needed to tell a genuine long-range catch
  // from a bad position. Same selection as daily (top 10 furthest + the
  // furthest in each 10-degree segment), which is exactly the set of extremes
  // worth auditing, rather than all 360 bearings.
  const { allTimeRangePolylines, allTimeRangeMarkers } = useMemo(() => {
    let polylines = [];
    let markers = [];

    // Skip if all-time range outline is disabled
    if (colourSettings.showAllTimeRangeOutline === false) {
      return { allTimeRangePolylines: polylines, allTimeRangeMarkers: markers };
    }

    // Pick the extremes worth showing: top 10 by distance, plus the furthest
    // record in each 10-degree bearing segment. Handles both payload shapes —
    // the WebSocket push uses `distance`, the HTTP snapshot uses `distanceKm`.
    const selectAllTimeMarkers = (records) => {
      const dist = recordDistanceKm;
      const top10 = [...records].sort((a, b) => dist(b) - dist(a)).slice(0, 10);
      const top10Set = new Set(top10.map(r => `${r.bearing}-${r.latitude}-${r.longitude}`));

      const segments = {};
      records.forEach(record => {
        const segmentIndex = Math.floor(record.bearing / 10);
        if (!segments[segmentIndex]) segments[segmentIndex] = [];
        segments[segmentIndex].push(record);
      });

      const segmentFurthest = [];
      Object.values(segments).forEach(segmentRecords => {
        if (segmentRecords.length === 0) return;
        const furthest = segmentRecords.reduce((max, r) => (dist(r) > dist(max) ? r : max));
        const key = `${furthest.bearing}-${furthest.latitude}-${furthest.longitude}`;
        if (!top10Set.has(key)) segmentFurthest.push(furthest);
      });

      return [...top10, ...segmentFurthest];
    };

    if (allTimeFurthestDistanceData && typeof allTimeFurthestDistanceData === 'object') {
      // Create a mapping from receiverUuid to receiverName
      const receiverUuidToName = {};
      receivers.forEach(receiver => {
        receiverUuidToName[receiver.receiverUuid] = receiver.receiverName;
      });

      // Iterate through each receiver's all-time data
      Object.entries(allTimeFurthestDistanceData).forEach(([receiverUuid, receiverData]) => {
        // Get receiver name from UUID
        const receiverName = receiverUuidToName[receiverUuid] || receiverUuid;

        // Skip if not selected (when a specific receiver is selected)
        if (selectedReceiver !== 'All Receivers' && receiverName !== selectedReceiver) {
          return;
        }

        if (!receiverData.bearingRecords || receiverData.bearingRecords.length === 0) {
          return;
        }

        // Sort records by bearing to ensure proper line connection
        const sortedRecords = [...receiverData.bearingRecords].sort((a, b) => a.bearing - b.bearing);

        // Clickable dots on the all-time outline, tinted with the all-time
        // colour so they're distinguishable from the daily ones.
        const allTimeColours = {
          ...colourSettings,
          maxDistanceOutline: colourSettings.allTimeRangeOutline || '#8B4513'
        };
        selectAllTimeMarkers(sortedRecords).forEach((record) => {
          markers.push(
            <FurthestDistanceMarker
              key={`alltime-${receiverUuid}-${record.bearing}-${record.latitude.toFixed(6)}-${record.longitude.toFixed(6)}`}
              record={record}
              colourSettings={allTimeColours}
              onSelect={setSelectedRangeRecord}
            />
          );
        });

        // Create polyline for this receiver's all-time range
        const positions = sortedRecords.map(record => [record.latitude, record.longitude]);
        // Close the loop by adding the first point at the end
        if (positions.length > 0) {
          positions.push(positions[0]);
        }

        const polyline = (
          <Polyline
            key={`alltime-polyline-${receiverUuid}`}
            positions={positions}
            pathOptions={{
              color: colourSettings.allTimeRangeOutline || '#8B4513',
              // Legible but recessive: weight 2 with moderate opacity. (0.9/w3
              // read as "too intense" over the map; 0.5/w2 was invisible.)
              weight: 2,
              opacity: 0.55,
              dashArray: '10, 6', // Dashed line to distinguish from daily
              interactive: false
            }}
          />
        );
        polylines.push(polyline);
      });
    }

    return { allTimeRangePolylines: polylines, allTimeRangeMarkers: markers };
  }, [allTimeFurthestDistanceData, selectedReceiver, receivers, colourSettings]);

  // Build track polylines from ground track points with phase-based colors (for Airport mode)
  const groundOperationPolylines = useMemo(() => {
    if (!groundTrackPoints?.trackPoints?.length) return [];

    const polylines = [];
    let currentPhase = null;
    let currentPoints = [];

    groundTrackPoints.trackPoints.forEach((point, index) => {
      const position = [point.latitude, point.longitude];

      if (point.phase !== currentPhase && currentPoints.length > 0) {
        // Save current segment
        polylines.push({
          positions: currentPoints,
          phase: currentPhase,
          color: TRACK_PHASE_COLORS[currentPhase] || '#888888',
          isEstimated: groundTrackPoints.trackPoints
            .slice(polylines.length > 0 ? polylines[polylines.length - 1].endIndex + 1 : 0, index)
            .some(p => p.isEstimated)
        });
        currentPoints = [currentPoints[currentPoints.length - 1]]; // Keep last point for continuity
      }

      currentPhase = point.phase;
      currentPoints.push(position);
    });

    // Add final segment
    if (currentPoints.length > 1) {
      polylines.push({
        positions: currentPoints,
        phase: currentPhase,
        color: TRACK_PHASE_COLORS[currentPhase] || '#888888',
        isEstimated: groundTrackPoints.trackPoints
          .slice(-currentPoints.length)
          .some(p => p.isEstimated)
      });
    }

    return polylines;
  }, [groundTrackPoints]);

  // Create parked aircraft markers for airport mode
  const parkedAircraftMarkers = useMemo(() => {
    if (!airportMode || !parkedAircraft || parkedAircraft.length === 0) return [];

    return parkedAircraft.map(aircraft => {
      // Use currentLat/currentLon for position
      if (!aircraft.currentLat || !aircraft.currentLon) return null;

      // Convert to format expected by AircraftMarker
      const aircraftForMarker = {
        hex: aircraft.icaoAddress,
        lat: aircraft.currentLat,
        lon: aircraft.currentLon,
        flight: aircraft.callsign,
        track: aircraft.heading || aircraft.track || 0,
        category: aircraft.category || aircraft.aircraftType,
        military: aircraft.military,
        // Pass through other fields
        registration: aircraft.registration,
        aircraftType: aircraft.aircraftType,
        airline: aircraft.airline,
        isNew: aircraft.isNew,
        groundState: aircraft.groundState,
        assignedGate: aircraft.assignedGate
      };

      return aircraftForMarker;
    }).filter(Boolean);
  }, [airportMode, parkedAircraft]);

  if (loading) {
    return <div className="map-loading">Loading map...</div>;
  }

  // Filter aircraft to only show those with valid positions on the map
  const aircraft = Object.values(aircraftData).filter(ac =>
    ac.lat != null && ac.lon != null
  );

  // Debug: Log new aircraft that should appear on the map
  // const newAircraftOnMap = aircraft.filter(ac => !ac.last_seen_at);
  // if (newAircraftOnMap.length > 0) {
  //   console.log('🆕 New aircraft on map:', newAircraftOnMap.map(ac => ({
  //     hex: ac.hex,
  //     flight: ac.flight,
  //     lat: ac.lat,
  //     lon: ac.lon,
  //     last_seen_at: ac.last_seen_at
  //   })));
  // }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <MapContainer
        center={position}
        zoom={zoom}
        zoomDelta={1}
        zoomSnap={1}
        wheelPxPerZoomLevel={120}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          key={tileLayerConfig.url} // Force re-render when URL changes
          attribution={tileLayerConfig.attribution}
          url={tileLayerConfig.url}
        />
        <TileFilterController filter={tileLayerConfig.filter} />
        <ResizeController resizeTrigger={resizeTrigger} autoZoom={autoZoom} />
        <FurthestDistancePaneController />
        <AirportFeaturesPaneController />
        <NotamPaneController />
        <ZoomHandler onZoomChange={handleZoomChange} />
        <ManualZoomDetector autoZoom={autoZoom} onAutoZoomChange={onAutoZoomChange} />
        <SelectionZoomController
          enabled={colourSettings.zoomOnAircraftSelect === true}
          selectedAircraftHex={selectedAircraftHex}
          trackData={aircraftTrack}
          aircraftData={aircraftData}
        />
        <AutoZoomController
          aircraftData={aircraftData}
          furthestDistanceData={furthestDistanceData}
          // While the selection zoom owns the view, auto-zoom stands down so the
          // two don't fight; it resumes as soon as the aircraft is deselected.
          autoZoom={selectionZoomActive ? 'none' : autoZoom}
          selectedReceiver={selectedReceiver}
          receivers={receivers}
          targetAirport={targetAirport}
          airportMode={airportMode}
          selectedAirport={selectedAirport}
          airportFeaturesData={airportFeaturesData}
          showParkingPositions={showParkingPositions}
        />
        <MapEventHandler onAirportsUpdate={onAirportsUpdate} />
        <ReceiverCenterController
          selectedReceiver={selectedReceiver}
          receivers={receivers}
        />
        {/* NOTAM overlay - circles, polygons, markers for active NOTAMs */}
        {colourSettings.showNotams !== false && (
          <NotamOverlay pane="notamPane" />
        )}
        {airportMarkers}
        {/* Render airport features: either from internal state (autoZoom='airport') or from external props (airportMode) */}
        {(autoZoom === 'airport' && airportFeatures) && (
          <AirportDetailOverlay
            features={airportFeatures}
            airport={targetAirport}
            pane="airportFeaturesPane"
          />
        )}
        {airportMode && airportFeaturesData && !(autoZoom === 'airport' && airportFeatures) && (
          <AirportDetailOverlay
            features={airportFeaturesData}
            airport={selectedAirport}
            pane="airportFeaturesPane"
            zoomLevel={showParkingPositions ? 'stands' : 'airport'}
          />
        )}
        {/* Ground track polylines for airport mode */}
        {airportMode && groundOperationPolylines.map((segment, index) => (
          <Polyline
            key={`ground-track-${index}`}
            positions={segment.positions}
            pathOptions={{
              color: segment.color,
              weight: 3,
              opacity: segment.isEstimated ? 0.5 : 0.9,
              dashArray: segment.isEstimated ? '5, 5' : null
            }}
          />
        ))}
        {/* Inbound airborne approach track for the selected aircraft.
            Drawn first (under the ground polyline) so the colour coding
            of the ground phases stays visible on top. */}
        {airportMode && inboundTrackPoints && inboundTrackPoints.length > 1 && (
          <Polyline
            key="inbound-track"
            positions={inboundTrackPoints}
            pathOptions={{
              color: '#f97316',   // orange — distinct from green/blue ground phase colours
              weight: 3,
              opacity: 0.85,
            }}
          />
        )}
        {/* Parked aircraft markers for airport mode */}
        {airportMode && parkedAircraftMarkers.map((ac) => (
          <AircraftMarker
            key={`parked-${ac.hex}`}
            aircraft={ac}
            isSelected={selectedAircraftHex === ac.hex}
            onAircraftSelect={onParkedAircraftSelect || onAircraftSelect}
            zoomLevel={currentZoom}
            autoZoom={autoZoom}
            selectedReceiver={selectedReceiver}
            colourSettings={colourSettings}
          />
        ))}
        {receivers.map((receiver) => (
          <ReceiverMarker
            key={receiver.receiverUuid}
            receiver={receiver}
            selectedReceiver={selectedReceiver}
          />
        ))}
        {aircraft.map((ac) => (
          <AircraftMarker
            key={ac.hex}
            aircraft={ac}
            isSelected={selectedAircraftHex === ac.hex}
            onAircraftSelect={onAircraftSelect}
            zoomLevel={currentZoom}
            autoZoom={autoZoom}
            selectedReceiver={selectedReceiver}
            colourSettings={colourSettings}
          />
        ))}
        {allTimeRangePolylines}
        {allTimeRangeMarkers}
        {furthestDistancePolylines}
        {furthestDistanceMarkers}
        {aircraftTrack && (
          <AircraftTrack
            trackData={aircraftTrack}
            selectedAircraftHex={selectedAircraftHex}
            liveAircraftData={aircraftData}
          />
        )}
      </MapContainer>
      {/* Top-right control stack: liquid-glass icon buttons; each opens an
          on-demand popup. Nothing else is always-present on the map. */}
      <div className="map-ctl-stack">
        {receiverOptions && onReceiverChange && (
          <ReceiverChip
            receivers={receiverOptions}
            selected={selectedReceiver}
            open={openPanel === 'receiver'}
            onClick={() => togglePanel('receiver')}
          />
        )}
        {onAutoZoomChange && (
          <button
            className={`map-ctl-btn ${openPanel === 'zoom' ? 'open' : ''} ${autoZoom !== 'none' ? 'active' : ''}`}
            onClick={() => togglePanel('zoom')}
            title="Auto zoom"
            aria-expanded={openPanel === 'zoom'}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /><path d="M8 11h6M11 8v6" />
            </svg>
          </button>
        )}
        <button
          className={`map-ctl-btn ${openPanel === 'filter' ? 'open' : ''} ${aircraftFilter !== 'all' ? 'active' : ''}`}
          onClick={() => togglePanel('filter')}
          title="Filters"
          aria-expanded={openPanel === 'filter'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
          </svg>
        </button>
        <button
          className={`map-ctl-btn ${openPanel === 'layers' ? 'open' : ''}`}
          onClick={() => togglePanel('layers')}
          title="Map layers"
          aria-expanded={openPanel === 'layers'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
        </button>
        {onToggleFullscreen && (
          <button
            className={`map-ctl-btn ${isFullscreen ? 'active' : ''}`}
            onClick={onToggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            )}
          </button>
        )}
        {onShowList && (
          <button className="map-ctl-btn" onClick={onShowList} title="Aircraft list">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
            </svg>
            {listCount > 0 && <span className="map-ctl-badge">{listCount}</span>}
          </button>
        )}
      </div>

      {openPanel && (
        <>
          <div className="map-filter-backdrop" onClick={() => setOpenPanel(null)} />
          <div className="map-filter-panel">
            {openPanel === 'receiver' && (
              <ReceiverOptions
                receivers={receiverOptions}
                selected={selectedReceiver}
                showAll={showAllReceiversOption}
                onSelect={(name) => { onReceiverChange(name); setOpenPanel(null); }}
              />
            )}

            {openPanel === 'zoom' && (
              <>
                <div className="map-filter-title">Auto zoom</div>
                {zoomOptions.map(([id, label]) => (
                  <button
                    key={id}
                    className={`map-filter-option ${autoZoom === id ? 'active' : ''}`}
                    onClick={() => { onAutoZoomChange(id); setOpenPanel(null); }}
                  >
                    <span className="map-filter-radio" aria-hidden="true" />
                    {label}
                  </button>
                ))}
                <div className="map-filter-divider" />
                <button
                  className={`map-filter-option ${colourSettings.zoomOnAircraftSelect === true ? 'active' : ''}`}
                  onClick={() => toggleOverlaySetting('zoomOnAircraftSelect', false)}
                  title="Fit the map to the selected aircraft's track (minimum 30 km). Restores the previous view when deselected."
                >
                  <span className="map-filter-check" aria-hidden="true" />
                  Zoom when aircraft selected
                </button>
              </>
            )}

            {openPanel === 'filter' && (
              <>
                {onAircraftFilterChange && (
                  <>
                    <div className="map-filter-title">Aircraft filter</div>
                    {filterOptions.map(([id, label]) => (
                      <button
                        key={id}
                        className={`map-filter-option ${aircraftFilter === id ? 'active' : ''}`}
                        onClick={() => { onAircraftFilterChange(id); setOpenPanel(null); }}
                      >
                        <span className="map-filter-radio" aria-hidden="true" />
                        {label}
                      </button>
                    ))}
                  </>
                )}
              </>
            )}

            {openPanel === 'layers' && (
              <>
                <div className="map-filter-title">Map layers</div>
                {overlayToggles.map(([key, label]) => (
                  <button
                    key={key}
                    className={`map-filter-option ${colourSettings[key] !== false ? 'active' : ''}`}
                    onClick={() => toggleOverlaySetting(key)}
                  >
                    <span className="map-filter-check" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
      {/* Render the popup as soon as an aircraft is selected so the user
          gets immediate feedback. Photo / country / airline metadata that
          come from the HTTP track-points response upgrade the popup when
          aircraftTrack arrives. Previously the popup blocked on the
          HTTP roundtrip and felt very slow to appear. */}
      {selectedAircraftHex && aircraftData?.[selectedAircraftHex] && (
        <AircraftInfoPopup
          trackData={aircraftTrack}
          selectedAircraftHex={selectedAircraftHex}
          liveAircraftData={aircraftData}
          onClose={() => onAircraftSelect && onAircraftSelect(null)}
        />
      )}
      {selectedRangeRecord && !selectedAircraftHex && (
        <AircraftInfoPopup
          trackData={rangeRecordTrackData}
          rangeRecord={rangeRecordTrackData || selectedRangeRecord}
          onClose={() => setSelectedRangeRecord(null)}
        />
      )}
    </div>
  );
});

export default Map;